import React from 'react';
import {twMerge} from 'tailwind-merge';
import {Check} from 'lucide-react';
import {getMenuItemIconSizeClass} from 'utils/uiSizes';
// Similar to: components/ui/NavMenu/NavMenuItem.js:line, Extract on 3rd occurrence
import MenuRow from 'components/ui/Menu/MenuRow';

/**
 * Reusable MenuItem component
 */
const MenuItem = React.forwardRef(
	(
		{
			option,
			index,
			getOptionLabel,
			getOptionValue,
			highlighted = false,
			selected = false,
			disabled = false,
			onClick,
			showCheck = true,
			size = 'md',
			variant = 'default',
			className,
			children,
			...props
		},
		ref,
	) => {
		const optionValue = getOptionValue(option);
		let optionLabel = getOptionLabel(option);
		// Safeguard: ensure optionLabel is always renderable
		if (
			optionLabel &&
			typeof optionLabel === 'object' &&
			!React.isValidElement(optionLabel)
		) {
			// If label is an object (not JSX), convert to string
			optionLabel = String(optionLabel);
		}
		const isDisabled = disabled || (option && option.disabled === true);
		const iconSizeClass = getMenuItemIconSizeClass(size);

		return (
			<MenuRow
				ref={ref}
				key={optionValue}
				size={size}
				disabled={isDisabled}
				highlighted={highlighted}
				variant={variant}
				className={twMerge(
					'hover:bg-gray-100 dark:hover:bg-gray-800',
					selected && 'text-gray-900 dark:text-gray-100 font-medium',
					selected &&
						isDisabled &&
						'bg-transparent opacity-80 text-gray-400 dark:text-gray-500 hover:bg-transparent hover:text-gray-400 dark:hover:text-gray-500',
					className,
				)}
				onClick={() => {
					if (!isDisabled) {
						onClick?.(option, optionValue);
					}
				}}
				role="option"
				aria-selected={selected}
				aria-disabled={isDisabled}
				id={`menuitem-option-${optionValue}`}
				{...props}
			>
				{children ? (
					<>
						<span className="flex-1 flex items-center gap-2 truncate">{children}</span>
						{selected && showCheck && (
							<Check
								className={twMerge(
									iconSizeClass,
									'text-blue-600 dark:text-blue-400 ml-2',
								)}
							/>
						)}
					</>
				) : (
					<>
						<span className="flex-1 flex items-center gap-2 truncate" title={optionLabel}>
							{optionLabel}
						</span>
						{selected && showCheck && (
							<Check
								className={twMerge(
									iconSizeClass,
									'text-blue-600 dark:text-blue-400 ml-2',
								)}
							/>
						)}
					</>
				)}
			</MenuRow>
		);
	},
);

MenuItem.displayName = 'MenuItem';

export default MenuItem;
